/**
 * Surface, field, text, radius, and shadow tokens for analysis-tool UI
 * (library pickers, query bar, notices, panels, and the Trip Analyzer views).
 *
 * The theme is the single source: styled-components read `theme.surface` and
 * `theme.shape` directly, and the same values are published as CSS custom
 * properties (`--color-card-bg`, `--radius-control`, …) through
 * `cssVariables`, for SCSS and inline styles that cannot read the theme.
 */

/** Theme-independent radius and elevation tokens. */
export const sharedShape = Object.freeze({
    radiusSm: '6px',
    radiusMd: '8px',
    radiusLg: '15px',
    // Match LytButton border-radius (LytBaseButton).
    radiusControl: '4px',
    shadowMd: '0 0 36px rgba(0, 1, 15, 0.05)',
    shadowLg: '0 18px 40px rgba(0, 1, 15, 0.28)',
});

const SURFACE_CSS_VARIABLES = Object.freeze({
    cardBg: '--color-card-bg',
    cardBorder: '--color-card-border',
    fieldBg: '--color-field-bg',
    fieldBorder: '--color-field-border',
    textPrimary: '--color-text-primary',
    textSecondary: '--color-text-secondary',
    textMuted: '--color-text-muted',
    accent: '--color-accent',
    dangerText: '--color-danger-text',
    tableHeaderBg: '--color-table-header-bg',
});

const SHAPE_CSS_VARIABLES = Object.freeze({
    radiusSm: '--radius-sm',
    radiusMd: '--radius-md',
    radiusLg: '--radius-lg',
    radiusControl: '--radius-control',
    shadowMd: '--shadow-md',
    shadowLg: '--shadow-lg',
});

/**
 * CSS custom properties for a theme's surface and shape tokens.
 *
 * @param {Record<string, string>} surface
 * @param {Record<string, string>} [shape]
 * @returns {Record<string, string>}
 */
export const buildSurfaceCssVariables = (surface, shape = sharedShape) => ({
    ...Object.fromEntries(Object.entries(SURFACE_CSS_VARIABLES).map(([key, name]) => [name, surface[key]])),
    ...Object.fromEntries(Object.entries(SHAPE_CSS_VARIABLES).map(([key, name]) => [name, shape[key]])),
});
